import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useCallPopStateSync, useCallPopStoreShallowStore } from '@frontend/pop';
import { useIpcShellProvider, shell } from '@frontend/shell-utils';
import { sentry } from '@frontend/tracking';
import { WeavePopNotification } from '@frontend/types';

/**
 * Referring to 'notifications' as everything that isn't a call pop. call pops are called 'pops' and the pop store manages them, but we expose the interaction here
 */
type ContextValue = {
  notifications: WeavePopNotification[];
  addNotification: (notification: WeavePopNotification) => void;
  removeNotification: (id: string) => void;
  removeAllNotifications: () => void;
  updateNotification: (notification: WeavePopNotification) => void;
  currNotificationIndex: number;
  setCurrNotificationIndex: (index: number) => void;
  currNotification: WeavePopNotification | undefined;

  pops: ReturnType<typeof useCallPopStoreShallowStore>['notifications'];
  addPop: ReturnType<typeof useCallPopStoreShallowStore>['addNotification'];
  removePop: ReturnType<typeof useCallPopStoreShallowStore>['removeNotification'];
  setPops: ReturnType<typeof useCallPopStoreShallowStore>['setNotifications'];
  popOutlet: ReturnType<typeof useCallPopStoreShallowStore>['outlet'];
  setPopOutlet: ReturnType<typeof useCallPopStoreShallowStore>['setOutlet'];
  currPopIndex: ReturnType<typeof useCallPopStoreShallowStore>['activeIndex'];
  setCurrPopIndex: ReturnType<typeof useCallPopStoreShallowStore>['setActiveIndex'];
};
const NotificationManagerContext = createContext<ContextValue>(undefined as unknown as ContextValue);

export const NotificationManagerProvider = ({ children }: { children: React.ReactNode }) => {
  const [notifications, setNotifications] = useState<WeavePopNotification[]>([]);
  const [currNotificationIndex, setCurrNotificationIndex] = useState(0);
  const currNotification = notifications[currNotificationIndex];
  const { status } = useIpcShellProvider();

  const addNotification = useCallback(
    (notification: WeavePopNotification) => {
      setNotifications((prev) => [...prev, notification]);
    },
    [setNotifications]
  );

  const removeNotification = useCallback(
    (id: string) => {
      setNotifications((prev) => {
        return prev.filter((n) => n.id !== id);
      });
    },
    [setNotifications]
  );

  const removeAllNotifications = useCallback(() => {
    setNotifications([]);
  }, [setNotifications]);

  const updateNotification = (notification: WeavePopNotification) => {
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === notification.id && n.type === notification.type
          ? ({ ...n, ...notification } as typeof notification)
          : n
      )
    );
  };

  //If the status disconnects, we should remove all the notifications
  useEffect(() => {
    if (status !== 'connected') {
      removeAllNotifications();
      pops.forEach((pop) => {
        removePop(pop.id);
      });

      const checkIfVersionIsNewer = (): boolean => {
        // Minimum required version: 2.0.87
        const MINIMUM_VERSION = [2, 0, 87];

        if (
          shell.version &&
          shell.version
            .split('.')
            .map(Number)
            .reduce((acc, num, idx) => (acc !== 0 ? acc : num - MINIMUM_VERSION[idx]), 0) >= 0
        ) {
          return true;
        }
        return false;
      };

      /* We are muting this for now till we need it later reference linear ticket FEDOP-2580 */
      if (status !== 'waiting' && status !== 'not_applicable' && checkIfVersionIsNewer()) {
        sentry.log({
          team: 'frontend-ops',
          message: `Removing all notifications because ipc status is not connected ${status} shell version: ${shell.version}`,
        });
      }
    }
  }, [status]);

  const {
    setOutlet: setPopOutlet,
    notifications: pops,
    setNotifications: setPops,
    addNotification: addPop,
    removeNotification: removePop,
    setActiveIndex: setCurrPopIndex,
    activeIndex: currPopIndex,
    outlet: popOutlet,
  } = useCallPopStateSync();

  const value = {
    notifications,
    addNotification,
    removeNotification,
    removeAllNotifications,
    updateNotification,
    currNotificationIndex,
    currNotification,
    setCurrNotificationIndex,

    pops,
    addPop,
    removePop,
    setPops,
    currPopIndex,
    setCurrPopIndex,
    popOutlet,
    setPopOutlet,
  } satisfies ContextValue;

  return <NotificationManagerContext.Provider value={value}>{children}</NotificationManagerContext.Provider>;
};

export const useNotificationManager = () => {
  const context = useContext(NotificationManagerContext);
  if (!context) {
    throw new Error('useNotificationManager must be used within a NotificationManagerProvider');
  }
  return context;
};
